/**
 * Brief 185 — a color-coded chip for a product (the activity stream's first
 * column). The product is the first dot-segment of a service key
 * (`llm.summarize` → `llm`). Unknown products fall back to a neutral chip, so
 * a newly-added product family never breaks the stream.
 */
import { cn } from '@/lib/utils';

// Map a product key → { label, classes }. Several cputools families
// (pdf/media/doc/image/csv/data) share the "Compute" color.
const PRODUCTS: Record<string, { label: string; cls: string }> = {
  baton: { label: 'Baton', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300' },
  esigndoc: { label: 'E-sign', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300' },
  esign: { label: 'E-sign', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300' },
  idverify: { label: 'ID verify', cls: 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300' },
  llm: { label: 'LLM', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300' },
  cputools: { label: 'Compute', cls: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300' },
  pdf: { label: 'PDF', cls: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300' },
  media: { label: 'Media', cls: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300' },
  doc: { label: 'Doc', cls: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300' },
  image: { label: 'Image', cls: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300' },
  csv: { label: 'CSV', cls: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300' },
  data: { label: 'Data', cls: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300' },
  bridge: { label: 'Courier', cls: 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300' },
  courier: { label: 'Courier', cls: 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300' },
};

export function productMeta(product: string | null): { label: string; cls: string } {
  if (!product) return { label: '—', cls: 'bg-muted text-muted-foreground' };
  return (
    PRODUCTS[product] ?? {
      label: product.charAt(0).toUpperCase() + product.slice(1),
      cls: 'bg-muted text-muted-foreground',
    }
  );
}

export function ProductChip({ product, className }: { product: string | null; className?: string }) {
  const { label, cls } = productMeta(product);
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        cls,
        className,
      )}
    >
      {label}
    </span>
  );
}
